import React, { useState, useEffect } from "react";
import "./RandomExamples.css";

const METAPHOR_COLORS = {
  "extension-of-self": "#667eea",
  "magical-tool": "#764ba2",
  person: "#f093fb",
};

const METAPHOR_LABELS = {
  "extension-of-self": "Extension of Self",
  "magical-tool": "Magical Tool",
  person: "Person",
};

const HighlightedText = ({ text, tags, metaphorColor }) => {
  if (!text || !tags) return text;

  try {
    const tagsList = JSON.parse(tags);
    if (!Array.isArray(tagsList) || tagsList.length === 0) return text;

    const parts = [];
    let lastIndex = 0;
    const textLower = text.toLowerCase();

    tagsList.forEach((tag) => {
      const tagLower = tag.toLowerCase();
      let searchIndex = 0;

      while (searchIndex < textLower.length) {
        const index = textLower.indexOf(tagLower, searchIndex);
        if (index === -1) break;

        if (index > lastIndex) {
          parts.push({
            text: text.slice(lastIndex, index),
            highlighted: false,
          });
        }
        parts.push({
          text: text.slice(index, index + tagLower.length),
          highlighted: true,
        });
        lastIndex = index + tagLower.length;
        searchIndex = lastIndex;
      }
    });

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), highlighted: false });
    }

    return (
      <>
        {parts.length > 0
          ? parts.map((part, i) => (
              <span
                key={i}
                style={
                  part.highlighted
                    ? {
                        backgroundColor: metaphorColor,
                        color: "white",
                        fontWeight: "bold",
                        padding: "2px 4px",
                        borderRadius: "3px",
                      }
                    : {}
                }
              >
                {part.text}
              </span>
            ))
          : text}
      </>
    );
  } catch (e) {
    return text;
  }
};

export default function RandomExamples() {
  const [data, setData] = useState(null);
  const [examples, setExamples] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/network-data.json")
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        pickRandomExamples(d);
        setLoading(false);
      })
      .catch((e) => {
        console.error("Error loading data:", e);
        setLoading(false);
      });
  }, []);

  const pickRandomExamples = (networkData) => {
    const getTagCount = (node) => {
      try {
        const tags = JSON.parse(node.metaphor_tags || "[]");
        return Array.isArray(tags) ? tags.length : 0;
      } catch {
        return 0;
      }
    };

    const pickBestCandidate = (candidates) => {
      if (candidates.length === 0) return null;

      // Group by tag count (1, 2, 3+)
      const byTagCount = {
        1: candidates.filter((n) => getTagCount(n) === 1),
        2: candidates.filter((n) => getTagCount(n) === 2),
        3: candidates.filter((n) => getTagCount(n) >= 3),
      };

      // Prioritize: 1 tag > 2 tags > 3+ tags
      for (const count of [1, 2, 3]) {
        if (byTagCount[count].length > 0) {
          return byTagCount[count][
            Math.floor(Math.random() * byTagCount[count].length)
          ];
        }
      }

      return null;
    };

    const ext = networkData.nodes.filter(
      (n) =>
        n.metaphorTypes.includes("extension-of-self") && getTagCount(n) > 0,
    );
    const tool = networkData.nodes.filter(
      (n) => n.metaphorTypes.includes("magical-tool") && getTagCount(n) > 0,
    );
    const person = networkData.nodes.filter(
      (n) => n.metaphorTypes.includes("person") && getTagCount(n) > 0,
    );

    const newExamples = {
      "extension-of-self": pickBestCandidate(ext),
      "magical-tool": pickBestCandidate(tool),
      person: pickBestCandidate(person),
    };

    setExamples(newExamples);
  };

  const handleRandomize = () => {
    if (data) {
      pickRandomExamples(data);
    }
  };

  if (loading) {
    return <div className="random-examples-container">Loading examples...</div>;
  }

  return (
    <div className="random-examples-container">
      <h2>Example Products</h2>
      <div className="examples-grid">
        {Object.keys(examples).map((metaphorType) => {
          const example = examples[metaphorType];
          if (!example) return null;

          return (
            <div key={metaphorType} className="example-card">
              <div
                className="example-header"
                style={{ backgroundColor: METAPHOR_COLORS[metaphorType] }}
              >
                <span className="metaphor-badge">
                  {METAPHOR_LABELS[metaphorType]}
                </span>
              </div>
              <div className="example-content">
                <h3>{example.name}</h3>
                {example.tagline && (
                  <p className="tagline">{example.tagline}</p>
                )}
                {example.description && (
                  <p className="description">
                    <HighlightedText
                      text={example.description}
                      tags={example.metaphor_tags}
                      metaphorColor={METAPHOR_COLORS[metaphorType]}
                    />
                  </p>
                )}
                {example.metaphorTypes && example.metaphorTypes.length > 0 && (
                  <div className="metaphor-tags">
                    {example.metaphorTypes.map((type) => (
                      <span
                        key={type}
                        className="tag"
                        style={{ backgroundColor: METAPHOR_COLORS[type] }}
                      >
                        {METAPHOR_LABELS[type]}
                      </span>
                    ))}
                  </div>
                )}
                {example.url && (
                  <a
                    href={example.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="product-link"
                  >
                    View on Product Hunt →
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="randomize-button-container">
        <button className="randomize-button" onClick={handleRandomize}>
          Get 3 more examples
        </button>
      </div>
    </div>
  );
}
